class RoomFilter extends HTMLElement {
  settings = {
    sectionId: 'room-inspiration-images',
    selectors: {
      containers: '.room-images-container',
      loadMoreBtn: '.load-more-btn',
      drawer: "[data-trigger-id='products-in-this-image_DrawerTrigger']",
      drawerBody: '.drawer__body',
      hotspot: '.inspiration-hotspots',
      image: '.inspiration-image',
      filterInput: 'input[name="room-filter"]'
    },
    state: {
      offsets: {},
      remaining: {},
      loaded: new Set()
    }
  };

  constructor() {
    super();
    const { containers, loadMoreBtn } = this.settings.selectors;

    this.containers = this.querySelectorAll(containers);
    this.loadMoreBtn = this.querySelector(loadMoreBtn);

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.loadMoreBtn?.addEventListener('click', this.onLoadMore);
    this.observe();
  }

  onClick = (e) => {
    if (window.innerWidth >= 750) return;
  
    const image = e.target.closest(`${this.settings.selectors.image}.hotspotenabled`);
    if (!image) return;
  
    const hotspot = image.querySelector(this.settings.selectors.hotspot);
    const drawerBody = document.querySelector(this.settings.selectors.drawer)?.querySelector(this.settings.selectors.drawerBody);
  
    if (hotspot && drawerBody) {
      drawerBody.closest('drawer-component').classList.add('active');
      drawerBody.replaceChildren(hotspot.cloneNode(true));
    }
  };
  
  onKeydown = (e) => {
    if (e.key === 'Enter') {
      const target = e.target;
      if (target.classList.contains('button-room-filter')) {
        target.previousElementSibling.click();
      }
    }
  };
  
  onChange = ({ target }) => {
    if (target.name !== 'room-filter') return;

    const room = target.value;
    this.querySelectorAll(this.settings.selectors.image).forEach(el => el.classList.add('hidden'));

    room === 'all' ? this.showAll() : this.showRoom(room);
  };

  showRoom(room) {
    if (this.settings.state.loaded.has(room)) {
      this.toggleRoom(room);
    } else {
      this.settings.state.offsets[room] ??= 0;
      this.fetchImages(room);
    }
  }

  showAll() {
    this.querySelectorAll(this.settings.selectors.image).forEach(el => el.classList.remove('hidden'));
    const promises = [];

    /* Dev: Cole: This line was assisted by AI */
    this.querySelectorAll(this.settings.selectors.filterInput).forEach(i => {
      const room = i.value;
      this.settings.state.offsets[room] ??= 0;
      if (!this.settings.state.loaded.has(room)) {
        promises.push(this.fetchImages(room));
      }
    });
    
    Promise.all(promises).then(() => this.updateLoadMore('all'));
    

    this.updateLoadMore('all');
  }

  toggleRoom(room) {
    this.querySelectorAll(`.js-${room}`).forEach(el => el.classList.remove('hidden'));
    this.updateLoadMore(room);
  }

  onLoadMore = () => {
    const room = this.querySelector('input[name="room-filter"]:checked')?.value;
    const fetchIfNeeded = r => (this.settings.state.remaining[r] ?? 0) > 0 && this.fetchImages(r);

    room === 'all'
      ? this.querySelectorAll(this.settings.selectors.filterInput).forEach(i => fetchIfNeeded(i.value))
      : fetchIfNeeded(room);
  };

  async fetchImages(room) {
    const { sectionId, state, selectors } = this.settings;
    const url = `/collections/all?filter.p.tag=${room}+${state.offsets[room]}&view=${sectionId}`;

    try {
      this.toggleLoading(true);
      const html = await fetch(url).then(res => res.text());
      const temp = document.createElement('div');
      temp.innerHTML = html;

      this.querySelector(selectors.containers).innerHTML += temp.innerHTML;
      state.loaded.add(room);
      state.remaining[room] = +temp.querySelector('[data-remaining-images]')?.dataset.remainingImages || 0;
      state.offsets[room]++;

      this.updateLoadMore(room);
    } catch (e) {
      console.error('Image load failed:', e);
    } finally {
      this.toggleLoading(false);
    }
  }

  toggleLoading(isLoading) {
    const loadingSpinner = this.querySelector('.loading__spinner');
    const loadMoreBtnSpinner = this.loadMoreBtn?.querySelector('.loading__spinner');
    
    if (isLoading) {
      loadingSpinner?.classList.remove('hidden');
      loadMoreBtnSpinner?.classList.remove('hidden');
    } else {
      loadingSpinner?.classList.add('hidden');
      loadMoreBtnSpinner?.classList.add('hidden');
    }
  }

  updateLoadMore(room) {
    const { remaining } = this.settings.state;
    const btn = this.loadMoreBtn;
    if (!btn) return;

    let totalRemaining = room === 'all'
      ? Object.values(remaining).reduce((sum, n) => sum + (n || 0), 0)
      : remaining[room] ?? 0;

    if (totalRemaining > 0) {
      const toLoadNext = Math.min(6, totalRemaining); // adjust "6" if your batch size is different
      btn.querySelector('.load-more-text').textContent = `Load ${toLoadNext} more`;
      btn.classList.remove('hidden');
    } else {
      btn.classList.add('hidden');
    }
  }

  observe() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    new IntersectionObserver(([entry], obs) => {
      if (entry.isIntersecting) {
        obs.unobserve(entry.target);
        this.querySelector(`${this.settings.selectors.filterInput}:checked`)
          ?.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, { rootMargin: '400px', threshold: 0.25 }).observe(section);
  }
}

customElements.define('room-filter', RoomFilter);
